import { take } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Subject, of, Observable } from 'rxjs';

import * as fromRoot from '../../../reducers';
import { environment } from '~environments/environment';
import * as LaunchDarkly from 'launchdarkly-js-client-sdk';
import { ReservationRetrievalObject } from '../../../models';

import * as _ from 'lodash';
import * as CryptoJS from 'crypto-js';
import { GatingServices } from './gating.services';
import * as featConfig from '../../../config/config.json';
import { LAUNCH_DARKLY_ENV_KEY, LAUNCH_DARKLY_SDK_KEY } from 'src/environments/environment';

@Injectable()
export class FeatureFlagsService {
	private readonly LAUNCH_DARKLY_ENV_KEY: any = null;
	private readonly LAUNCH_DARKLY_SDK_KEY: any = null;

	private launchDarklyClient: LaunchDarkly.LDClient;
	private launchDarklyFlags: LaunchDarkly.LDFlagSet;
	private readonly featureConfig: {[key: string]: object | null} = featConfig;
	private featureFlags: {[key: string]: any} | null = null;

	constructor(private readonly store: Store<fromRoot.AppState>,
		private gatingService: GatingServices) {
		if (LAUNCH_DARKLY_ENV_KEY) {
			this.LAUNCH_DARKLY_ENV_KEY = LAUNCH_DARKLY_ENV_KEY;
			this.LAUNCH_DARKLY_SDK_KEY = LAUNCH_DARKLY_SDK_KEY;
		}
    this.initFeatureFlags();
	}

	createHash(userKey: string) {
		const hmac = CryptoJS.algo.HMAC.create(CryptoJS.algo.SHA256, this.LAUNCH_DARKLY_SDK_KEY);
		hmac.update(userKey);
		const finalHmac = hmac.finalize();
		return finalHmac.toString(CryptoJS.enc.Hex);
	}

	initialize(reservationData: any): Observable<any>  {
		const subject = new Subject<LaunchDarkly.LDFlagSet>();

		if (!this.LAUNCH_DARKLY_ENV_KEY || !this.LAUNCH_DARKLY_SDK_KEY) {
			return of(null);
		}

		const date = new Date(`${reservationData.departureYear}-${reservationData.departureMonth}-${reservationData.departureDay}`).getTime();

		const context: LaunchDarkly.LDContext = {
			key: reservationData.vdsId,
			anonymous: true,
			custom: {
				platform: 'web',
				device: navigator.userAgent,
				shipCode: reservationData.shipCode,
				sailDate: date,
				departurePort: reservationData.departurePort,
				email: reservationData.email,
				sessions: 1,
				brand: reservationData.brand,
				realShipCode: 'none'
			}
		};

		const hash = this.createHash(reservationData.vdsId);

		this.launchDarklyClient = LaunchDarkly.initialize(this.LAUNCH_DARKLY_ENV_KEY, context, {
			hash: hash
		});

		this.launchDarklyClient.on('ready', () => {
			this.launchDarklyFlags = this.launchDarklyClient.allFlags();
			subject.next(this.launchDarklyFlags);
		});

		return subject;
	}

	get snapshot() {
		let result: any = null;

		this.store
			.pipe(
				select(fromRoot.getFeatureFlagsState),
				take(1)
			)
			.subscribe(flags => {
				const reservationData: ReservationRetrievalObject = this.gatingService.getReservationData();
				if (!(_.get(reservationData, 'charterSailing', false)) && _.get(flags, 'appFeaturePopup', false)) {
					result = Object.assign({}, flags, { appFeaturePopup: true });
				}
				else {
					result = Object.assign({}, flags, { appFeaturePopup: false });
				}
				result = Object.assign(result ?? {}, flags, { OLCICreditCardOnly: _.get(flags, 'olci-credit-card-only', false) });
			});

		return result;
	}

  private initFeatureFlags(): void {
    const {envName = ''} = environment || {};
    const env = envName.split('.')[1];
    this.featureFlags = this.getActiveFeatures(env);
  }

  getActiveFeatures(environmentParam: string): object | null {
    return this.featureConfig[environmentParam] || this.featureConfig['prod'];
  }

  isFeatureEnabled(feature: string): boolean {
    // This works for feature flags present in config file, for LaunchDarkly flags use snapshot
    return this.featureFlags && this.featureFlags[feature];
  }
}
